import React, { Component } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { Tooltip, Select, Input, Button, message } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  DISTRO_FAMILY,
  COMMON_VERSIONS,
  generateConfig,
  needsVersion,
} from "../../../utils/mirrorTemplates";
import "./configGeneratorCard.css";

/**
 * "How to use mirror" jeneratörü
 * — mirrors.json'dan canlı dağıtım listesi
 * — family auto-detect → doğru template (deb/rpm/arch/alpine/opensuse/generic)
 * — sürüm: yaygın listeden dropdown veya manuel input
 */
export default class ConfigGeneratorCard extends Component {
  state = {
    visible: false,
    mirrors: [],            // mirrors.json'dan
    distro: undefined,      // seçili dağıtım
    version: undefined,     // seçili sürüm (preset)
    customVersion: '',      // manuel sürüm
    output: '',             // üretilen config metni
  };

  /* Modal açılınca canlı dağıtım listesi çek */
  componentDidMount() {
    axios.get('/mirrors.json')
      .then(({ data }) => {
        // Sadece şablon desteklenen olanlar (DISTRO_FAMILY tablosunda var)
        const supported = (data || [])
          .filter(m => DISTRO_FAMILY[m.name])
          .map(m => m.name)
          .sort();
        this.setState({ mirrors: supported });
      })
      .catch(() => { /* mirrors.json gelmezse boş kal */ });
  }

  show   = () => this.setState({ visible: true });
  cancel = () => this.setState({
    visible: false,
    distro: undefined,
    version: undefined,
    customVersion: '',
    output: '',
  });

  onDistroChange = (distro) => {
    // Yeni dağıtım seçilince sürüm ve çıktıyı sıfırla
    this.setState({ distro, version: undefined, customVersion: '', output: '' });
  };

  onGenerate = () => {
    const { distro, version, customVersion } = this.state;
    if (!distro) {
      message.warning('Please pick a mirror first');
      return;
    }
    const v = customVersion.trim() || version || '';
    if (needsVersion(distro) && !v) {
      message.warning('Please choose a version');
      return;
    }
    this.setState({ output: generateConfig(distro, v) });
  };

  render() {
    const { visible, mirrors, distro, version, customVersion, output } = this.state;
    const versions = distro ? (COMMON_VERSIONS[distro] || []) : [];
    const showVersion = distro && needsVersion(distro);
    const family = distro ? DISTRO_FAMILY[distro]?.family : null;

    const modal = visible
      ? ReactDOM.createPortal(
          <div className="fb-modal-backdrop" onClick={this.cancel}>
            <div
              className="fb-modal fb-modal--config"
              onClick={e => e.stopPropagation()}
            >
              {/* Başlık */}
              <div className="fb-modal__head">
                <h2 className="fb-modal__title">
                  <ThunderboltOutlined style={{ marginRight: 8, fontSize: 16 }} />
                  Mirror Config Generator
                </h2>
                <button className="fb-modal__close" onClick={this.cancel}>✕</button>
              </div>

              {/* Body */}
              <div className="fb-modal__body fb-modal__body--config">

                {/* Dağıtım seçimi */}
                <div className="cfg-field">
                  <label className="cfg-label">Mirror</label>
                  <Select
                    showSearch
                    placeholder="Select a mirror"
                    value={distro}
                    onChange={this.onDistroChange}
                    style={{ width: '100%' }}
                    getPopupContainer={() => document.body}
                    optionFilterProp="children"
                  >
                    {mirrors.map(m => (
                      <Select.Option key={m} value={m}>
                        {m}{' '}
                        <span className="cfg-family-tag">
                          {DISTRO_FAMILY[m]?.family}
                        </span>
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {/* Sürüm seçimi */}
                {showVersion && (
                  <div className="cfg-field">
                    <label className="cfg-label">
                      Version
                      {versions.length === 0 && (
                        <span className="cfg-hint"> (type manually)</span>
                      )}
                    </label>
                    {versions.length > 0 ? (
                      <Select
                        placeholder="Select a version"
                        value={version}
                        onChange={(v) => this.setState({ version: v, customVersion: '' })}
                        style={{ width: '100%' }}
                        getPopupContainer={() => document.body}
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <div className="cfg-custom">
                              <Input
                                placeholder="…or type a custom version"
                                value={customVersion}
                                onChange={(e) => this.setState({
                                  customVersion: e.target.value,
                                  version: undefined,
                                })}
                                onPressEnter={this.onGenerate}
                              />
                            </div>
                          </>
                        )}
                      >
                        {versions.map(v => (
                          <Select.Option key={v} value={v}>{v}</Select.Option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        placeholder="e.g. jammy, 9, edge"
                        value={customVersion}
                        onChange={(e) => this.setState({ customVersion: e.target.value })}
                        onPressEnter={this.onGenerate}
                      />
                    )}
                  </div>
                )}

                {/* Aile bilgisi */}
                {distro && (
                  <div className="cfg-info">
                    Family: <strong>{family}</strong>
                    {!showVersion && ' · No version needed'}
                  </div>
                )}

                {/* Üret butonu */}
                <Button
                  type="primary"
                  block
                  onClick={this.onGenerate}
                  disabled={!distro}
                >
                  Generate config
                </Button>

                {/* Çıktı bloğu */}
                {output && (
                  <div className="config-block">
                    <Tooltip placement="top" title="Click to copy">
                      <CopyToClipboard
                        text={output}
                        onCopy={() => message.success('Copied!', 1)}
                      >
                        <pre style={{ margin: 0 }}>{output}</pre>
                      </CopyToClipboard>
                    </Tooltip>
                  </div>
                )}

              </div>

              {/* Footer */}
              <div className="fb-modal__foot">
                <span className="fb-modal__hint">
                  Pick a mirror, choose a version, then Generate.
                </span>
                <button
                  className="cta cta--secondary"
                  style={{ padding: '0.45rem 1.25rem', fontSize: '0.85rem' }}
                  onClick={this.cancel}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

    return (
      <>
        <button className="cta cta--secondary" onClick={this.show}>
          <ThunderboltOutlined style={{ marginRight: 8 }} />
          How to use mirror
        </button>
        {modal}
      </>
    );
  }
}
